import { useEffect, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { useFeature } from 'src/app/FeatureToggle';
import type { RootState } from 'src/app/store/types';
import {
  PatchedOrganizationSettings,
  useOrganizationSettingsPartialUpdateMutation,
  useOrganizationSettingsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { assignDefaultFormValues } from './assignDefaultFormValues';
import BufferTimeSettings from './BufferTimeSettings';
import { CustomizationSettings } from './CustomizationSettings';
import ExportSettings from './ExportSettings';
import { formValuesToPatchedOrganization } from './formValuesToPatchedOrganization';
import { generateErrorToast } from './generateErrorToast';
import { generateSuccessToast } from './generateSuccessToast';
import LoadUnloadSettings from './LoadUnloadSettings';
import LocationSettings from './LocationSettings';
import { MobileAppSettings } from './MobileAppSettings';
import PermissionSettings from './PermissionSettings';
import PortalSettings from './PortalSettings';
import PricingSettings from './PricingSettings';
import SignatureSettings from './SignatureSettings';
import TransportationSettings from './TransportationSettings';
import { TripRequiredFieldSettings } from './TripRequiredFieldSettings';
import { TripSettings } from './TripSettings';
import VehicleInspections from './VehicleInspections';

export default function OrganizationSettingsForm() {
  const dispatch = useDispatch();
  const nextToastId = useSelector((state: RootState) => state.toast.nextId);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isEnabled: isTripRequiredFieldsEnabled } = useFeature(
    'trip required fields'
  );

  const {
    data: organizationSettings,
    isLoading: isLoadingOrganizationSettings,
  } = useOrganizationSettingsRetrieveQuery({});

  const [saveOrganizationSettings] =
    useOrganizationSettingsPartialUpdateMutation();

  const methods = useForm();

  const submit = async () => {
    setIsSubmitting(true);
    const values = methods.getValues();

    // Save logo separately because we need to construct a form data
    try {
      // Check for null or file
      if (
        'logo' in values &&
        (values.logo === null || !!values.logo) &&
        typeof values.logo !== 'string'
      ) {
        const formData = new FormData();
        formData.append('logo', values.logo === null ? '' : values.logo);

        await saveOrganizationSettings({
          patchedOrganizationSettings:
            formData as unknown as PatchedOrganizationSettings,
        }).unwrap();
        dispatch(
          show({
            title: `Success`,
            description: 'The logo has been updated.',
            type: 'success',
          })
        );
      }
    } catch (e) {
      dispatch(
        show({
          title: `Failure`,
          description: `Unable to upload logo: ${formatServerError(e)}`,
          type: 'error',
        })
      );
    }

    // Save remaining data
    try {
      const patchedOrganizationSettings =
        formValuesToPatchedOrganization(values);

      await saveOrganizationSettings({
        patchedOrganizationSettings,
      }).unwrap();
      dispatch(show(generateSuccessToast(nextToastId)));
    } catch {
      dispatch(show(generateErrorToast(nextToastId)));
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    methods.reset(assignDefaultFormValues(organizationSettings));
  }, [methods, organizationSettings]);

  return (
    <>
      {isLoadingOrganizationSettings ? (
        <div className="flex h-[80vh] flex-col items-center justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <FormProvider {...methods}>
          <div className="pb-[72px]">
            <form onSubmit={methods.handleSubmit(submit)}>
              <LocationSettings />
              <ExportSettings />
              <TransportationSettings />
              <SignatureSettings />
              {isTripRequiredFieldsEnabled && <TripRequiredFieldSettings />}
              <VehicleInspections />
              <PermissionSettings />
              <LoadUnloadSettings />
              <BufferTimeSettings />
              <TripSettings />
              <PortalSettings />
              <PricingSettings />
              <MobileAppSettings />
              <CustomizationSettings />
              <div className="fixed bottom-0 left-0 right-0 flex h-[64px] items-center justify-end border-t border-gray-900/10 bg-white px-4">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Saving Organization Settings...'
                    : 'Save Organization Settings'}
                </Button>
              </div>
            </form>
          </div>
        </FormProvider>
      )}
    </>
  );
}
